/**
 * Merges backend validation config on top of the frontend SearchFormatConfig.
 * Backend is the single source of truth — frontend values are fallbacks only.
 */

import type { SearchFormatConfig, ConditionalWarning } from '../search-config';
import type { BackendSearchMediaConfig } from '@/api/searchValidationConfig';

const VALID_WHEN_VALUES: Set<ConditionalWarning['when']> = new Set([
  'hasImages',
  'hasVideos',
  'hasLongHeadlines',
  'hasCompanionBanner',
  'hasNeither',
]);

const VALID_TEXT_FIELDS: Set<string> = new Set(['headlines', 'longHeadlines', 'descriptions']);

/**
 * Overlay backend validation rules onto a frontend config.
 * Returns a new config object (does not mutate the original).
 */
export function mergeBackendConfig(
  frontendConfig: SearchFormatConfig,
  backendConfig: BackendSearchMediaConfig | undefined,
): SearchFormatConfig {
  if (!backendConfig) return { ...frontendConfig };

  const merged = { ...frontendConfig };

  // ── Text fields ─────────────────────────────────────────────────────────
  if (backendConfig.text_fields) {
    const tf = backendConfig.text_fields;

    if (tf.headlines && merged.headlines) {
      merged.headlines = {
        ...merged.headlines,
        min: tf.headlines.min,
        max: tf.headlines.max,
        charLimit: tf.headlines.char_limit,
      };
    }
    if (tf.longHeadlines && merged.longHeadlines) {
      merged.longHeadlines = {
        ...merged.longHeadlines,
        min: tf.longHeadlines.min,
        max: tf.longHeadlines.max,
        charLimit: tf.longHeadlines.char_limit,
      };
    }
    if (tf.descriptions && merged.descriptions) {
      merged.descriptions = {
        ...merged.descriptions,
        min: tf.descriptions.min,
        max: tf.descriptions.max,
        charLimit: tf.descriptions.char_limit,
      };
    }
    if (tf.displayUrlPaths && merged.displayUrlPaths) {
      merged.displayUrlPaths = {
        ...merged.displayUrlPaths,
        min: tf.displayUrlPaths.min,
        max: tf.displayUrlPaths.max,
        charLimit: tf.displayUrlPaths.char_limit,
      };
    }
  }

  // ── Media slots (min/max only — dimensions/accept/ratios stay frontend) ─
  if (backendConfig.slots && merged.mediaSlots) {
    const mergedSlots = { ...merged.mediaSlots };
    for (const [key, backendSlot] of Object.entries(backendConfig.slots)) {
      if (mergedSlots[key]) {
        mergedSlots[key] = {
          ...mergedSlots[key],
          min: backendSlot.min,
          max: backendSlot.max,
          optional: backendSlot.optional ?? mergedSlots[key].optional,
        };
        if (backendSlot.recommended_count !== undefined) {
          mergedSlots[key].recommendedCount = backendSlot.recommended_count;
        }
        if (backendSlot.recommended_text !== undefined) {
          mergedSlots[key].recommended = backendSlot.recommended_text;
        }
      }
    }
    merged.mediaSlots = mergedSlots;
  }

  // ── Shared image max ────────────────────────────────────────────────────
  if (backendConfig.shared_image_max !== undefined) {
    merged.sharedImageMax = backendConfig.shared_image_max;
  }

  // ── Final URL requirement ───────────────────────────────────────────────
  if (backendConfig.final_url !== undefined) {
    merged.finalUrl = backendConfig.final_url;
  }

  // ── Validation rules ────────────────────────────────────────────────────
  if (backendConfig.validation_rules) {
    const vr = backendConfig.validation_rules;
    merged.validationRules = {
      oneHeadlineMaxChars: vr.one_headline_max_chars ?? undefined,
      recommendedDescriptions: vr.recommended_descriptions ?? undefined,
      longHeadlineMinWarning: vr.long_headline_min_warning ?? undefined,
      videoRecommended: vr.video_recommended ?? undefined,
    };
  }

  // ── CTA (min/max only — options stay frontend) ──────────────────────────
  if (backendConfig.cta && merged.cta) {
    merged.cta = {
      ...merged.cta,
      min: backendConfig.cta.min,
      max: backendConfig.cta.max,
    };
  }

  // ── Conditional warnings (fully replaced from backend) ──────────────────
  // Runtime-validates `when` and `textField` to prevent crashes from unexpected values.
  if (backendConfig.conditional_warnings) {
    merged.conditionalWarnings = backendConfig.conditional_warnings
      .filter((cw) => VALID_WHEN_VALUES.has(cw.when as ConditionalWarning['when']))
      .filter((cw) => !cw.text_field || VALID_TEXT_FIELDS.has(cw.text_field))
      .map(
        (cw): ConditionalWarning => ({
          when: cw.when as ConditionalWarning['when'],
          textField: cw.text_field as ConditionalWarning['textField'],
          mediaSlots: cw.media_slots,
          warnKey: cw.warn_key,
          message: cw.message,
        }),
      );
  }

  return merged;
}
